import { produce } from 'immer'
import { isEmpty, omit, omitBy } from 'lodash/fp'
import { SearchState } from 'shared/types/Search'

interface ISeoMapping {
  filterType: FilterType
  filterName: string
  urlName: string
}

enum FilterType {
  RefinementList,
  Range,
}

const createSeoMapping = (
  filterType: FilterType,
  filterName: string,
  urlName: string,
): ISeoMapping => ({ filterType, filterName, urlName })

const SeoMappingList: ISeoMapping[] = [
  createSeoMapping(FilterType.RefinementList, 'brand_name', 'brand'),
  createSeoMapping(FilterType.RefinementList, 'silhouette', 'model'),
  createSeoMapping(FilterType.RefinementList, 'presentation_size', 'size'),
  createSeoMapping(FilterType.RefinementList, 'size_us_men', 'size_men'),
  createSeoMapping(FilterType.RefinementList, 'size_us_women', 'size_women'),
  createSeoMapping(FilterType.RefinementList, 'size_us_youth', 'size_youth'),
  createSeoMapping(FilterType.RefinementList, 'shoe_condition', 'condition'),
  createSeoMapping(FilterType.RefinementList, 'color', 'color'),
  createSeoMapping(FilterType.RefinementList, 'single_gender', 'gender'),
  createSeoMapping(FilterType.RefinementList, 'category', 'category'),
  createSeoMapping(FilterType.RefinementList, 'product_category', 'product_category'),
  createSeoMapping(FilterType.RefinementList, 'product_type', 'product_type'),
  createSeoMapping(FilterType.RefinementList, 'designer', 'designer'),
  createSeoMapping(FilterType.RefinementList, 'collection_slugs', 'collection'),
  createSeoMapping(FilterType.Range, 'lowest_price_cents', 'price_cents'),
  createSeoMapping(FilterType.Range, 'lowest_price_cents_usd', 'price_cents_usd'),
  createSeoMapping(FilterType.Range, 'release_year', 'year'),
]

const omitByEmptyValues = omitBy(isEmpty)

const omitRefinementList = omitBy((refinement) => {
  const isRefinementEmpty = isEmpty(refinement)
  const isEmptyArray = Array.isArray(refinement) && isEmpty(omitByEmptyValues(refinement))

  return isRefinementEmpty || isEmptyArray
})

const adjustRefinementListProperties = (searchState: Record<string, unknown>) => {
  if (!isEmpty(searchState.refinementList)) {
    SeoMappingList.filter((x) => x.filterType === FilterType.RefinementList).forEach(
      (mapping) => {
        if (
          searchState.refinementList![mapping.filterName] &&
          !isEmpty(omitByEmptyValues(searchState.refinementList![mapping.filterName]))
        ) {
          searchState[mapping.urlName] = searchState.refinementList![mapping.filterName].join(',')
        }
      },
    )
  }
}

const adjustRangeProperties = (searchState: Record<string, unknown>) => {
  if (!isEmpty(searchState.range)) {
    SeoMappingList.filter((x) => x.filterType === FilterType.Range).forEach((mapping) => {
      if (searchState.range![mapping.filterName]) {
        if (searchState.range![mapping.filterName].min) {
          searchState[`${mapping.urlName}_min`] = searchState.range![mapping.filterName].min
        }
        if (searchState.range![mapping.filterName].max) {
          searchState[`${mapping.urlName}_max`] = searchState.range![mapping.filterName].max
        }
      }
    })
  }
}

const defaultSearchState: SearchState = {
  page: 1,
  query: undefined,
  range: {},
  refinementList: {},
}

export const searchStateToUrl = (searchState: SearchState) => {
  if (isEmpty(searchState)) {
    return {}
  }

  return produce(searchState, (draftUrlParams: Record<string, unknown>) => {
    draftUrlParams.refinementList = omitRefinementList(draftUrlParams.refinementList)

    if (draftUrlParams.page === 1) {
      delete draftUrlParams.page
    }

    if (draftUrlParams.configure) {
      draftUrlParams.configure = omit(['hitsPerPage', 'distinct', 'filters', 'ruleContexts'])(
        draftUrlParams.configure,
      )
    }

    if (isEmpty(draftUrlParams.configure)) {
      delete draftUrlParams.configure
    }

    // Friendly SEO for refinement list
    adjustRefinementListProperties(draftUrlParams)

    // Friendly SEO for range
    adjustRangeProperties(draftUrlParams)

    delete draftUrlParams.refinementList
    delete draftUrlParams.range
  })
}

export const urlToSearchState = (urlParams: Record<string, unknown>): SearchState => {
  if (isEmpty(urlParams)) {
    return defaultSearchState
  }

  const { query = '', page = 1 } = urlParams

  const refinementList: Record<string, string[]> = {}
  const range: Record<string, { min: number; max: number }> = {}

  SeoMappingList.forEach((mapping) => {
    // for refinementList object
    if (mapping.filterType === FilterType.RefinementList && urlParams[mapping.urlName]) {
      const rawValue = urlParams[mapping.urlName]
      // `qs` does not return an array when there's a single value.
      const refinementValue = Array.isArray(rawValue)
        ? rawValue
        : rawValue.split(',').filter(Boolean)
      refinementList[mapping.filterName] = refinementValue.map(decodeURIComponent)
    }
    // for range object
    if (
      mapping.filterType === FilterType.Range &&
      (urlParams[`${mapping.urlName}_min`] || urlParams[`${mapping.urlName}_max`])
    ) {
      range[mapping.filterName] = {
        min: urlParams[`${mapping.urlName}_min`],
        max: urlParams[`${mapping.urlName}_max`],
      }
    }
  })

  return {
    ...defaultSearchState,
    page,
    query: query ? decodeURIComponent(query) : undefined,
    range,
    refinementList,
  }
}
